import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, Typography, Popper, Fade } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { ChartWrapper } from 'src/components';
import { getChartData } from 'src/scenes/Project/components/ecosystemUtils';


const useStyles = makeStyles(theme => ({
  chartContainer: {
    height: '400px',
    position: 'relative',
  },
  tooltip: {
    backgroundColor: theme.palette.grey[800],
    color: theme.palette.common.white,
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.spacing(0.5),
    maxWidth: 250,
    fontSize: '0.75rem',
    zIndex: 2000,
  },
}));

const EcosystemTrendsChart = ({ ecosystems, viewMode = 'formation', metric: propMetric = 'adjustedArea' }) => {
  const classes = useStyles();
  const [ metric, setMetric ] = useState(propMetric);
  const [ tooltipOpen, setTooltipOpen ] = useState(false);
  const [ tooltipContent, setTooltipContent ] = useState({ title: '', description: '' });
  const [ anchorEl, setAnchorEl ] = useState(null);

  useEffect(() => {
    setMetric(propMetric);
  }, [ propMetric ]);

  const getYAxisLabel = selectedMetric => {
    switch (selectedMetric) {
      case 'area':
      case 'adjustedArea':
        return 'Superficie (hectáreas)';
      case 'condition':
        return 'Índice de Condición (0-1)';
      default:
        return 'Valor';
    }
  };

  const getFormationComposition = formationName => {
    const removeAccents = str => str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    const compositions = {
      'vegetacion natural': [ 'Praderas', 'Bosques', 'Matorral', 'Humedal' ],
      'semi-natural': [ 'Agrícultura', 'Plantación forestal' ],
      'artificial': [ 'Infraestructura' ],
      'cuerpos de agua': [ 'Río, lagos, oceano', 'Nieves y glaciares' ],
      'otros': [ 'Playa, arena, dunas', 'Áreas desprovistas de vegetación', 'Afloramiento rocoso', 'Salares' ],
    };
    const normalizedName = removeAccents(formationName?.toString().trim().toLowerCase() || '');
    return compositions[normalizedName] || [];
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        onHover: (evt, legendItem, legend) => {
          if (viewMode === 'formation' && legendItem) {
            legend.chart.canvas.style.cursor = 'help';
            const composition = getFormationComposition(legendItem.text);
            if (composition.length > 0) {
              // Crear un referenceObject compatible con Popper.js
              const rect = legend.chart.canvas.getBoundingClientRect();
              const virtualElement = {
                getBoundingClientRect: () => ({
                  top: rect.top + evt.y,
                  left: rect.left + evt.x,
                  bottom: rect.top + evt.y,
                  right: rect.left + evt.x,
                  width: 0,
                  height: 0,
                }),
                clientWidth: 0,
                clientHeight: 0,
                addEventListener: () => {},
                removeEventListener: () => {},
              };
              setTooltipContent({ title: legendItem.text, description: composition });
              setAnchorEl(virtualElement);
              setTooltipOpen(true);
            }
          }
        },
        onLeave: (evt, legendItem, legend) => {
          legend.chart.canvas.style.cursor = 'default';
          setTooltipOpen(false);
          setAnchorEl(null);
        },
      },
      tooltip: {
        callbacks: {
          label: context => {
            const label = context.dataset.label || '';
            let value;
            if (metric === 'condition') {
              value = context.parsed.y.toFixed(2);
            } else {
              value = Math.round(context.parsed.y).toLocaleString('es-CL', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              });
            }
            return `${label}: ${value}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ...(metric === 'condition' && { max: 1.0 }),
        title: {
          display: true,
          text: getYAxisLabel(metric),
        },
        ticks: {
          callback: function(value) {
            if (metric === 'condition') {
              return value.toFixed(2);
            } else {
              return Math.round(value).toLocaleString('es-CL', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              });
            }
          },
        },
      },
      x: {
        title: {
          display: true,
          text: 'Año',
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
    elements: {
      point: {
        radius: 4,
        hoverRadius: 6,
      },
    },
  };

  return (
    <Box className={classes.chartContainer}>
      <ChartWrapper
        type="line"
        data={getChartData(ecosystems, metric, viewMode)}
        options={chartOptions}
      />

      <Popper open={tooltipOpen} anchorEl={anchorEl} placement="top-start" transition>
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={200}>
            <Paper className={classes.tooltip} elevation={4}>
              <Typography variant="subtitle2" component="div">
                <strong>{tooltipContent.title}</strong>
              </Typography>
              <Box component="ul" m={0} pl={2}>
                {Array.isArray(tooltipContent.description)
                  ? tooltipContent.description.map((item, index) => (
                    <Typography key={index} variant="caption" component="li">
                      {item}
                    </Typography>
                  ))
                  :
                  <Typography variant="caption" component="div">
                    {tooltipContent.description}
                  </Typography>
                }
              </Box>
            </Paper>
          </Fade>
        )}
      </Popper>
    </Box>
  );
};

EcosystemTrendsChart.propTypes = {
  viewMode: PropTypes.string,
  metric: PropTypes.oneOf([ 'area', 'condition', 'adjustedArea' ]),
  ecosystems: PropTypes.object.isRequired,
};


export {
  EcosystemTrendsChart,
};
